import { KlausurTableOfContents } from "@/components/klausurTOC.component";
import { ResizablePanels } from "@/components/ui/resizable-panels";
import { Button } from "@/components/ui/button";
import type {
	NavigationAufgabenstellungDTO,
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import KorrekturbogenEditor from "./components/korrekturbogenEditor.component";
import PDFViewer from "./components/pdfViewer.component";
import { useLayout } from "@/lib/contexts/layout.context";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import KorrekturService from "./korrektur.service";
import type {
	AufgabenstellungDTO,
	KorrekturStudentKlausurWithKorrekturStepDTO,
	SachverhaltDTO,
} from "./korrektur.types";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LoadingSpinner from "@/components/loadingSpinner.component";
import { AnnotationService } from "@/lib/annotations/annotation.service";
import { Maximize, Minimize, Send } from "lucide-react";
import ScrollableContainer from "@/components/scrollableContainer.component";
import { useSubmitKorrekturConfirmDialog } from "./hooks/useSubmitKorrekturConfirmDialog";

export default function KorrekturPage() {
	const { korrekturStepId } = useParams();
	const { setShowNavbar } = useLayout();

	const [korrektur, setKorrektur] =
		useState<KorrekturStudentKlausurWithKorrekturStepDTO | null>(null);
	const [navigationKlausur, setNavigationKlausur] =
		useState<NavigationKlausurDTO | null>(null);
	const [activeAufgabenstellung, setActiveAufgabenstellung] =
		useState<AufgabenstellungDTO | null>(null);
	const [activeSachverhalt, setActiveSachverhalt] =
		useState<SachverhaltDTO | null>(null);
	const [sortedSachverhalte, setSortedSachverhalte] = useState<
		SachverhaltDTO[] | null
	>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [currentAnnotations, setCurrentAnnotations] =
		useState<InstantJSON | null>(null);
	const initialAnnotations = useRef<InstantJSON | null>(null);
	const [annotationsReady, setAnnotationsReady] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
	const [isTablet, setIsTablet] = useState(false);
	const [dontShowAgain, setDontShowAgain] = useState(false);
	const korrekturService = KorrekturService.getInstance();
	const navigate = useNavigate();

	useEffect(() => {
		setShowNavbar(false);
		// Prevent body scrolling on iPad/mobile
		document.body.classList.add("prevent-scroll");
		return () => {
			setShowNavbar(true);
			document.body.classList.remove("prevent-scroll");
		};
	}, [setShowNavbar]);

	// Detect if device is a tablet
	useEffect(() => {
		const checkIfTouchDevice = () => {
			return "ontouchstart" in window || navigator.maxTouchPoints > 0;
		};

		setIsTablet(checkIfTouchDevice());
	}, []);

	// Show fullscreen prompt on tablets (unless user disabled it)
	useEffect(() => {
		if (isTablet && annotationsReady) {
			const dontShow = localStorage.getItem("korrektur-fullscreen-dont-show");
			if (!dontShow) {
				// Delay prompt slightly to not overwhelm on page load
				const timer = setTimeout(() => {
					setShowFullscreenPrompt(true);
				}, 1000);
				return () => clearTimeout(timer);
			}
		}
	}, [isTablet, annotationsReady]);

	// Handle fullscreen changes
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);

	// Exit fullscreen when leaving the page
	useEffect(() => {
		return () => {
			if (document.fullscreenElement) {
				document.exitFullscreen().catch((error) => {
					console.error("Error exiting fullscreen on unmount:", error);
				});
			}
		};
	}, []);

	const toggleFullscreen = async () => {
		try {
			if (!document.fullscreenElement) {
				await document.documentElement.requestFullscreen();
				toast.success("Vollbildmodus aktiviert");
			} else {
				await document.exitFullscreen();
				toast.info("Vollbildmodus deaktiviert");
			}
		} catch (error) {
			console.error("Fullscreen error:", error);
			toast.error("Vollbildmodus konnte nicht aktiviert werden");
		}
	};

	const handleFullscreenPromptAction = async (enterFullscreen: boolean) => {
		setShowFullscreenPrompt(false);

		if (dontShowAgain) {
			localStorage.setItem("korrektur-fullscreen-dont-show", "true");
		}

		if (enterFullscreen) {
			await toggleFullscreen();
		}
	};

	/**
	 * Get the korrekturUserKlausurData for the current korrekturStepId and also get the korrekturStep data for the current klausur
	 */
	useEffect(() => {
		if (!korrekturStepId) return;
		setAnnotationsReady(false);

		korrekturService
			.getKorrekturUserKlausurData(korrekturStepId)
			.then(async (data) => {
				setKorrektur(data);
				setNavigationKlausur(data);
				if (data.aufgabenstellungen.length > 0) {
					setActiveAufgabenstellung(data.aufgabenstellungen[0]);
					if (data.aufgabenstellungen[0].sachverhalte.length > 0) {
						setActiveSachverhalt(data.aufgabenstellungen[0].sachverhalte[0]);
					}
				}

				// If klausur is locked, do not load annotations
				if (data.locked) {
					initialAnnotations.current = null;
					setCurrentAnnotations(null);
					setAnnotationsReady(true);
					return;
				}

				try {
					const fetched =
						await AnnotationService.getInstance().getPdfAnnotationsForKorrekturStudentKlausur(
							data.id,
						);
					if (fetched) {
						initialAnnotations.current = fetched;
						setCurrentAnnotations(fetched);
						// Validate InstantJSON format
						if (
							initialAnnotations.current.format !==
							"https://pspdfkit.com/instant-json/v1"
						) {
							initialAnnotations.current = null;
							setCurrentAnnotations(null);
						}
					} else {
						initialAnnotations.current = null;
						setCurrentAnnotations(null);
					}
				} catch (err) {
					console.error("Error fetching annotations:", err);
					initialAnnotations.current = null;
					setCurrentAnnotations(null);
				} finally {
					setAnnotationsReady(true);
				}
			});
	}, [korrekturService, korrekturStepId]);

	useEffect(() => {
		if (activeAufgabenstellung) {
			setSortedSachverhalte(
				activeAufgabenstellung.sachverhalte.sort((a, b) => a.index - b.index),
			);
		}
	}, [activeAufgabenstellung]);

	/**
	 * Sneds the korrektur to the server
	 */
	const handleSubmitKorrektur = async () => {
		if (!korrekturStepId) return;

		try {
			setIsSubmitting(true);
			await korrekturService.submitKorrektur(korrekturStepId);
			toast.success("Korrektur erfolgreich abgegeben");
			navigate(-1);
		} catch (error) {
			console.error("Error submitting korrektur:", error);
			toast.error("Fehler beim Abgeben der Korrektur");
		} finally {
			setIsSubmitting(false);
		}
	};

	const submitConfirmDialog = useSubmitKorrekturConfirmDialog({
		isSubmitting,
		onConfirmSubmit: handleSubmitKorrektur,
	});

	if (
		!navigationKlausur ||
		!korrektur ||
		!korrekturStepId ||
		!annotationsReady
	) {
		return <LoadingSpinner />;
	}

	/**
	 * TODO: This will set the annotations from the selected history entry to the current annotations
	 * Handles the selection of a history entry
	 * @param entry - The history entry
	 */
	// const handleHistoryEntrySelect = (entry: AnnotationHistoryDTO) => {
	// 	setCurrentAnnotations(entry.annotations ?? null);
	// 	toast.info("Annotationen aus dem Verlauf geladen.", {
	// 		duration: 1000,
	// 	});
	// };

	/**
	 * Handles the selection of an aufgabenstellung
	 * @param aufgabenstellung - The aufgabenstellung
	 */
	const handleAufgabenstellungClick = (
		aufgabenstellung: NavigationAufgabenstellungDTO,
	) => {
		const aufgabenstellungDTO = korrektur.aufgabenstellungen.find(
			(a) => a.id === aufgabenstellung.id,
		);
		if (aufgabenstellungDTO) {
			setActiveAufgabenstellung(aufgabenstellungDTO);
			setActiveSachverhalt(aufgabenstellungDTO.sachverhalte[0]);
		}
	};

	const handleSachverhaltClick = (sachverhalt: NavigationSachverhaltDTO) => {
		const sachverhaltDTO = korrektur.aufgabenstellungen
			.find((a) => a.id === activeAufgabenstellung?.id)
			?.sachverhalte.find((s) => s.id === sachverhalt.id);
		if (sachverhaltDTO) {
			// Search for the aufgabenstellung of the sachverhaltDTO
			const aufgabenstellungDTO = korrektur.aufgabenstellungen.find((a) =>
				a.sachverhalte.some((s) => s.id === sachverhaltDTO.id),
			);
			if (aufgabenstellungDTO) {
				setActiveAufgabenstellung(aufgabenstellungDTO);
			}
			setActiveSachverhalt(sachverhaltDTO);
		}
	};

	/**
	 * Handles the update of the points for a korrekturbogen item
	 * @param korrekturKorrekturbogenItemId - The id of the korrekturbogen item
	 * @param userPoints - The points to update
	 * @returns Promise with the boolean result
	 */
	const handleKorrekturbogenPointsUpdate = async (
		korrekturKorrekturbogenItemId: string,
		userPoints: number,
	) => {
		const result = await korrekturService.updateKorrekturbogenItemPoints(
			korrekturStepId,
			korrekturKorrekturbogenItemId,
			userPoints,
		);
		if (!result) {
			toast.error("Fehler beim Aktualisieren der Punkte");
		}
	};

	/**
	 * Handles the update of the points for a musterloesung
	 * @param korrekturMusterloesungId - The id of the musterloesung
	 * @param userPoints - The points to update
	 * @returns Promise with the boolean result
	 */
	const handleMusterloesungPointsUpdate = async (
		korrekturMusterloesungId: string,
		userPoints: number,
	) => {
		const result = await korrekturService.updateMusterloesungPoints(
			korrekturStepId,
			korrekturMusterloesungId,
			userPoints,
		);
		if (!result) {
			toast.error("Fehler beim Aktualisieren der Musterlösung-Punkte");
		}
	};

	const leftPanel = (
		<div className="overflow-y-auto h-full bg-background-secondary rounded-lg">
			{/* Panels per Sachverhalt */}
			{sortedSachverhalte && sortedSachverhalte.length > 0 ? (
				sortedSachverhalte.map((sachverhalt) => (
					<div
						key={sachverhalt.id}
						data-item-id={sachverhalt.id}
						className="rounded-t-lg flex bg-background-secondary flex-col"
					>
						<h3 className="text-md font-medium bg-background-secondary p-2 sticky top-0 z-10">
							{sachverhalt.title}
						</h3>
						<div className=" bg-white flex flex-col gap-4 p-2 mb-4 pb-2 last:border-b-4">
							{/* For each musterloesung in the sachverhalt, create a new KorrekturbogenEditor, order by index musterloesung and korrekturbogenItem index */}
							{sachverhalt.musterloesungen
								.sort((a, b) => a.index - b.index)
								.map((musterloesung) => (
									<KorrekturbogenEditor
										key={musterloesung.id}
										title={musterloesung.title}
										musterloesungId={musterloesung.korrekturMusterloesungId}
										musterloesungPoints={musterloesung.userPoints}
										korrekturbogenItems={musterloesung.korrekturbogenItems.sort(
											(a, b) => a.index - b.index,
										)}
										onPointsUpdate={handleKorrekturbogenPointsUpdate}
										onMusterloesungPointsUpdate={
											handleMusterloesungPointsUpdate
										}
										readOnly={korrektur.locked}
									/>
								))}
						</div>
					</div>
				))
			) : (
				<div className="p-4 text-center text-gray-500">
					Keine Sachverhalte verfügbar
				</div>
			)}
		</div>
	);

	const rightPanel = (
		<ScrollableContainer showInitialHint={true} hintDuration={3000}>
			<PDFViewer
				korrekturStudentKlausurId={korrektur.id}
				className="w-full h-full"
				korrekturStepId={korrekturStepId}
				annotations={currentAnnotations}
				inkColorHex={korrektur.korrekturStep?.color}
				readOnly={korrektur.locked}
			/>
		</ScrollableContainer>
	);

	return (
		<div className="flex flex-1 overflow-hidden px-2 h-full w-full">
			{/* Fullscreen Prompt */}
			{showFullscreenPrompt && (
				<div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex items-center gap-3 mb-4">
							<Maximize className="w-6 h-6 text-blue-600" />
							<h2 className="text-xl font-semibold">
								Vollbildmodus aktivieren?
							</h2>
						</div>
						<p className="text-gray-600 mb-4">
							Für ein besseres Korrekturen-Erlebnis auf Tablets empfehlen wir
							den Vollbildmodus. Dadurch wird die Browser-Leiste ausgeblendet
							und Sie haben mehr Platz zum Arbeiten.
						</p>
						<label className="flex items-center gap-2 mb-6 cursor-pointer">
							<input
								type="checkbox"
								checked={dontShowAgain}
								onChange={(e) => setDontShowAgain(e.target.checked)}
								className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
							/>
							<span className="text-sm text-gray-700">Nicht mehr anzeigen</span>
						</label>
						<div className="flex gap-3">
							<Button
								onClick={() => handleFullscreenPromptAction(true)}
								className="flex-1"
								variant="default"
							>
								<Maximize className="w-4 h-4 mr-2" />
								Vollbild aktivieren
							</Button>
							<Button
								onClick={() => handleFullscreenPromptAction(false)}
								className="flex-1"
								variant="outline"
							>
								Später
							</Button>
						</div>
					</div>
				</div>
			)}

			<div className="h-full flex-shrink-0 mr-4 mt-2 flex flex-col justify-between">
				<div className="absolute right-2 top-2 z-50 flex gap-2">
					{/* Fullscreen toggle button for tablets */}
					{isTablet && (
						<Button
							onClick={toggleFullscreen}
							variant="outline"
							size="icon"
							className="shrink-0"
							title={
								isFullscreen
									? "Vollbildmodus beenden"
									: "Vollbildmodus aktivieren"
							}
						>
							{isFullscreen ? (
								<Minimize className="w-4 h-4" />
							) : (
								<Maximize className="w-4 h-4" />
							)}
						</Button>
					)}
					<Button
						type="button"
						onClick={submitConfirmDialog.open}
						disabled={isSubmitting || korrektur.locked}
						className="w-full"
						variant="default"
					>
						<Send className="w-4 h-4" />
						{korrektur.locked
							? "Bereits beendet"
							: isSubmitting
								? "Wird abgegeben..."
								: "Abgeben"}
					</Button>
					{submitConfirmDialog.dialog}
				</div>
				<div className="overflow-y-auto">
					<KlausurTableOfContents
						klausur={navigationKlausur}
						activeAufgabenstellung={activeAufgabenstellung}
						activeSachverhalt={activeSachverhalt}
						onAufgabenstellungClick={handleAufgabenstellungClick}
						onSachverhaltClick={handleSachverhaltClick}
						backButton={true}
						isCollapsed={true}
					/>
				</div>
			</div>
			<div className="flex-1 flex overflow-hidden">
				<ResizablePanels
					leftPanel={leftPanel}
					rightPanel={rightPanel}
					initialLeftWidth={40}
					minLeftWidth={20}
					maxLeftWidth={80}
					className="flex-1 rounded-lg overflow-hidden py-4"
				/>
			</div>
		</div>
	);
}
